import {Badge} from '@dropbox/dig-components/badge';
import {Text} from '@dropbox/dig-components/typography';
import {Box} from '@dropbox/dig-foundations';
import cx from 'classnames';
import type {Column} from 'components/DSYS/Table';
import {Table} from 'components/DSYS/Table';
import {Title} from 'components/DSYS/Title';
import type {ReactNode} from 'react';
import styles from 'views/roadmap/Roadmap.module.css';
import type {RoadmapItem} from 'views/roadmap/roadmapData';
import type {LaunchTableRow} from 'views/roadmap/types';

interface OverviewTabProps {
  launches: {
    recent: RoadmapItem[];
    overdue: RoadmapItem[];
    upcoming: RoadmapItem[];
    cancelled: RoadmapItem[];
  };
  launchColumns: Column<LaunchTableRow>[];
  renderLaunchRow: (row: LaunchTableRow, showOverdue?: boolean) => ReactNode;
}

export const OverviewTab = ({launches, launchColumns, renderLaunchRow}: OverviewTabProps) => (
  <section className={styles.section}>
    <div className={styles.launchGrid}>
      <div className={styles.dashboardLaunchSection}>
        <div className={styles.dashboardSectionHeader}>
          <Title size={18}>Recent</Title>
        </div>
        <Table<LaunchTableRow>
          columns={launchColumns}
          data={launches.recent.map((item) => ({id: item.key, item}))}
          headerTextSize="small"
          columnLabelOverrides={{
            workstream: 'Workstream',
            initiative: 'Initiative',
            owner: 'DRI',
            status: 'Status',
            date: 'End Date',
            links: 'Resources',
          }}
          className={cx(styles.table, styles.dashboardTable)}
        >
          <Table.Header />
          <Table.Body className={styles.tableBody}>
            {launches.recent.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={launchColumns.length}>
                  <Box padding="24" display="flex" justifyContent="center">
                    <Text color="subtle">No recent launches.</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            ) : (
              launches.recent.map((item) => renderLaunchRow({id: item.key, item}))
            )}
          </Table.Body>
        </Table>
      </div>

      <div className={styles.dashboardLaunchSection}>
        <div className={styles.dashboardSectionHeader}>
          <div className={styles.dashboardSectionTitle}>
            <Title size={18}>Upcoming</Title>
            <Badge size="xsmall">{launches.upcoming.length}</Badge>
          </div>
        </div>
        <Table<LaunchTableRow>
          columns={launchColumns}
          data={launches.upcoming.map((item) => ({id: item.key, item}))}
          headerTextSize="small"
          columnLabelOverrides={{
            workstream: 'Workstream',
            initiative: 'Initiative',
            owner: 'DRI',
            status: 'Status',
            date: 'End Date',
            links: 'Resources',
          }}
          className={cx(styles.table, styles.dashboardTable)}
        >
          <Table.Header />
          <Table.Body className={styles.tableBody}>
            {launches.upcoming.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={launchColumns.length}>
                  <Box padding="24" display="flex" justifyContent="center">
                    <Text color="subtle">No upcoming launches.</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            ) : (
              launches.upcoming.map((item) => renderLaunchRow({id: item.key, item}, true))
            )}
          </Table.Body>
        </Table>
      </div>

      <div className={styles.dashboardLaunchSection}>
        <div className={styles.dashboardSectionHeader}>
          <div className={styles.dashboardSectionTitle}>
            <Title size={18}>Overdue</Title>
            <Badge size="xsmall">{launches.overdue.length}</Badge>
          </div>
        </div>
        <Table<LaunchTableRow>
          columns={launchColumns}
          data={launches.overdue.map((item) => ({id: item.key, item}))}
          headerTextSize="small"
          columnLabelOverrides={{
            workstream: 'Workstream',
            initiative: 'Initiative',
            owner: 'DRI',
            status: 'Status',
            date: 'End Date',
            links: 'Resources',
          }}
          className={cx(styles.table, styles.dashboardTable)}
        >
          <Table.Header />
          <Table.Body className={styles.tableBody}>
            {launches.overdue.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={launchColumns.length}>
                  <Box padding="24" display="flex" justifyContent="center">
                    <Text color="subtle">No overdue launches.</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            ) : (
              launches.overdue.map((item) => renderLaunchRow({id: item.key, item}, true))
            )}
          </Table.Body>
        </Table>
      </div>

      <div className={styles.dashboardLaunchSection}>
        <div className={styles.dashboardSectionHeader}>
          <div className={styles.dashboardSectionTitle}>
            <Title size={18}>Cancelled</Title>
            <Badge size="xsmall">{launches.cancelled.length}</Badge>
          </div>
        </div>
        <Table<LaunchTableRow>
          columns={launchColumns}
          data={launches.cancelled.map((item) => ({id: item.key, item}))}
          headerTextSize="small"
          columnLabelOverrides={{
            workstream: 'Workstream',
            initiative: 'Initiative',
            owner: 'DRI',
            status: 'Status',
            date: 'End Date',
            links: 'Resources',
          }}
          className={cx(styles.table, styles.dashboardTable)}
        >
          <Table.Header />
          <Table.Body className={styles.tableBody}>
            {launches.cancelled.length === 0 ? (
              <Table.Row>
                <Table.Cell colSpan={launchColumns.length}>
                  <Box padding="24" display="flex" justifyContent="center">
                    <Text color="subtle">No cancelled launches.</Text>
                  </Box>
                </Table.Cell>
              </Table.Row>
            ) : (
              launches.cancelled.map((item) => renderLaunchRow({id: item.key, item}))
            )}
          </Table.Body>
        </Table>
      </div>
    </div>
  </section>
);
